import {createAction, props} from '@ngrx/store';
import {UUID} from '../../shared/types/uuid.type';
import {
  CreateTranscodingPresetDto,
  TranscodingPreset
} from '../../services/api/transcoding-presets';

/**
 * Action prefix for transcoding presets
 */
const ACTION_PREFIX = '[Transcoding Presets]';

// ==================== Load All Presets ====================

/**
 * Initiates loading of all transcoding presets
 */
export const loadTranscodingPresets = createAction(
  `${ACTION_PREFIX} Load All`
);

/**
 * Successfully loaded all transcoding presets
 */
export const loadTranscodingPresetsSuccess = createAction(
  `${ACTION_PREFIX} Load All Success`,
  props<{ presets: TranscodingPreset[] }>()
);

/**
 * Failed to load transcoding presets
 */
export const loadTranscodingPresetsFailure = createAction(
  `${ACTION_PREFIX} Load All Failure`,
  props<{ error: string }>()
);

// ==================== Load Single Preset by ID ====================

/**
 * Initiates loading of a single transcoding preset by ID
 */
export const loadTranscodingPresetById = createAction(
  `${ACTION_PREFIX} Load By ID`,
  props<{ id: UUID }>()
);

/**
 * Successfully loaded a single transcoding preset
 */
export const loadTranscodingPresetByIdSuccess = createAction(
  `${ACTION_PREFIX} Load By ID Success`,
  props<{ preset: TranscodingPreset }>()
);

/**
 * Failed to load a transcoding preset by ID
 */
export const loadTranscodingPresetByIdFailure = createAction(
  `${ACTION_PREFIX} Load By ID Failure`,
  props<{ id: UUID; error: string }>()
);

// ==================== Create Preset ====================

/**
 * Initiates creation of a new transcoding preset
 */
export const createTranscodingPreset = createAction(
  `${ACTION_PREFIX} Create`,
  props<{ dto: CreateTranscodingPresetDto }>()
);

/**
 * Successfully created a transcoding preset
 */
export const createTranscodingPresetSuccess = createAction(
  `${ACTION_PREFIX} Create Success`,
  props<{ preset: TranscodingPreset }>()
);

/**
 * Failed to create transcoding preset
 */
export const createTranscodingPresetFailure = createAction(
  `${ACTION_PREFIX} Create Failure`,
  props<{ error: string }>()
);

// ==================== Delete Preset ====================

/**
 * Initiates deletion of a transcoding preset
 */
export const deleteTranscodingPreset = createAction(
  `${ACTION_PREFIX} Delete`,
  props<{ id: UUID }>()
);

/**
 * Successfully deleted a transcoding preset
 */
export const deleteTranscodingPresetSuccess = createAction(
  `${ACTION_PREFIX} Delete Success`,
  props<{ id: UUID }>()
);

/**
 * Failed to delete transcoding preset
 */
export const deleteTranscodingPresetFailure = createAction(
  `${ACTION_PREFIX} Delete Failure`,
  props<{ id: UUID; error: string }>()
);

// ==================== UI State Management ====================

/**
 * Selects a transcoding preset (for UI focus/editing)
 */
export const selectTranscodingPreset = createAction(
  `${ACTION_PREFIX} Select`,
  props<{ id: UUID | null }>()
);

/**
 * Clears all errors from the state
 */
export const clearTranscodingPresetErrors = createAction(
  `${ACTION_PREFIX} Clear Errors`
);

/**
 * Resets the entire transcoding presets state to initial values
 */
export const resetTranscodingPresetsState = createAction(
  `${ACTION_PREFIX} Reset State`
);
